import fuzzysort from 'fuzzysort';
import { useCallback, useEffect, useMemo } from 'react';
import { MediaStatusType } from '@lib2/model/media/MediaTypes';
import { MediaUtils } from '../MediaUtils';
import { VideoFilterUtils, type VideoFilterCandidate } from '../videoFilters';
import { useVideoGridState } from '../stores/videoGridState';
import { recordDuration, recordSample } from '@/shared/utils/perf';
import type { SharedLinkConfig } from '@/lib/types/link';
import type {
  SearchMode,
  SortDirection,
  VideoFilters,
  VideoGridRow,
  VideoSortKey,
  VideoViewData,
} from '@/lib/types/Media';

const GRID_INDEX_CACHE_LIMIT = 3;
const SEARCH_CACHE_LIMIT = 12;
const ROW_CACHE_LIMIT = 3;
const ROW_VIEW_MODEL_CACHE_LIMIT = 6000;

const STATUS_SORT_RANK: Record<string, number> = {
  [MediaStatusType.AwaitingIngest]: 0,
  [MediaStatusType.Ingesting]: 1,
  [MediaStatusType.Processing]: 2,
  [MediaStatusType.Ready]: 3,
  [MediaStatusType.Error]: 4,
  [MediaStatusType.Deleted]: 5,
};

interface SortState {
  key: VideoSortKey;
  direction: SortDirection;
}

interface SearchState {
  mode: SearchMode;
  text: string;
}

interface UseVideoGridEngineArgs {
  customerId: number | null;
  videos: VideoViewData[];
  sort: SortState;
  search: SearchState;
  filters: VideoFilters;
  linkConfig: SharedLinkConfig | undefined;
  defaultPlayerId: number | null;
  /** Account brand (white-label vs direct) — selects the content host baked into row URLs. */
  isReseller: boolean;
}

interface TimingStats {
  searchMs: number;
  filterMs: number;
  sortMs: number;
  sortBuildMs: number;
  totalMs: number;
}

interface DatasetRecord {
  id: number;
  source: VideoViewData;
  title: string;
  refId: string | null;
  descShort: string | null;
  descLong: string | null;
  tags: string[];
  status: string;
  state: string;
  published: number;
  updated: number;
  duration: number;
  lowerTitle: string | null;
  lowerRefId: string | null;
  lowerDescShort: string | null;
  lowerDescLong: string | null;
  lowerTags: string[] | null;
  statusRank: number | null;
}

interface SortOrder {
  asc: number[];
  desc: number[];
}

interface FuzzyPreparedRecord {
  id: number;
  preparedTitle: ReturnType<typeof fuzzysort.prepare>;
}

interface FuzzyPreparedRefIdRecord {
  id: number;
  preparedRefId: ReturnType<typeof fuzzysort.prepare>;
}

/** One prepared target per record for an optional text field (tags joined, descriptions). */
interface FuzzyPreparedFieldRecord {
  id: number;
  prepared: ReturnType<typeof fuzzysort.prepare>;
}

interface VideoGridIndex {
  cacheKey: string;
  signature: string;
  idToVideo: Map<number, VideoViewData>;
  sourceOrderIds: number[];
  sourceOrderIsUpdatedDesc: boolean;
  records: DatasetRecord[];
  sortOrders: Partial<Record<VideoSortKey, SortOrder>>;
  exactSearchCache: Map<string, Set<number>>;
  fuzzySearchCache: Map<string, Set<number>>;
  filterCache: Map<string, Set<number>>;
  fuzzyPreparedRecords: FuzzyPreparedRecord[] | null;
  fuzzyPreparedRefIds: FuzzyPreparedRefIdRecord[] | null;
  fuzzyPreparedTags: FuzzyPreparedFieldRecord[] | null;
  fuzzyPreparedDescShorts: FuzzyPreparedFieldRecord[] | null;
  fuzzyPreparedDescLongs: FuzzyPreparedFieldRecord[] | null;
  lastUsedAt: number;
}

interface VideoGridIndexResult {
  index: VideoGridIndex;
  cacheHit: boolean;
}

interface ComputeResult {
  ids: number[];
  timings: TimingStats;
}

interface RowCacheEntry {
  source: VideoViewData;
  contextKey: string;
  row: VideoGridRow;
}

export interface VideoGridEngineStats {
  searchMs: number;
  sortMs: number;
  totalMs: number;
  mode: 'main';
  workerFallback: false;
}

export interface UseVideoGridEngineResult {
  count: number;
  orderedIds: number[];
  getIdByIndex: (index: number) => number | undefined;
  getRowByIndex: (index: number) => VideoGridRow | null;
  resultCount: number;
  isComputing: boolean;
  stats: VideoGridEngineStats;
}

const indexCache = new Map<string, VideoGridIndex>();
const persistedRowCaches = new Map<string, Map<number, RowCacheEntry>>();

function now(): number {
  return typeof performance !== 'undefined' ? performance.now() : Date.now();
}

function getStatusRank(status: string): number {
  const rank = STATUS_SORT_RANK[status];
  return rank == null ? Number.MAX_SAFE_INTEGER : rank;
}

function getDatasetSignature(videos: VideoViewData[]): string {
  const count = videos.length;
  if (count === 0) return '0:0:0:0';

  const firstId = videos[0]?.media.mediaId ?? 0;
  const lastId = videos[count - 1]?.media.mediaId ?? 0;
  let maxUpdated = 0;

  for (const video of videos) {
    if (video.media.updated > maxUpdated) {
      maxUpdated = video.media.updated;
    }
  }

  return `${count}:${maxUpdated}:${firstId}:${lastId}`;
}

function getOrderQueryKey(sort: SortState, search: SearchState, filterKey: string): string {
  return `${search.mode}:${search.text.trim().toLowerCase()}:${sort.key}:${sort.direction}:${filterKey}`;
}

function tagsEqual(a: string[], b: string[]): boolean {
  if (a === b) return true;
  if (a.length !== b.length) return false;
  for (let index = 0; index < a.length; index += 1) {
    if (a[index] !== b[index]) return false;
  }
  return true;
}

function isSourceOrderUpdatedDesc(videos: VideoViewData[]): boolean {
  for (let index = 1; index < videos.length; index += 1) {
    const prev = videos[index - 1]?.media;
    const next = videos[index]?.media;
    if (!prev || !next) {
      return false;
    }

    if (prev.updated < next.updated) {
      return false;
    }

    if (prev.updated === next.updated && prev.mediaId < next.mediaId) {
      return false;
    }
  }

  return true;
}

function touchIndex(cacheKey: string, index: VideoGridIndex): void {
  index.lastUsedAt = now();
  indexCache.delete(cacheKey);
  indexCache.set(cacheKey, index);

  while (indexCache.size > GRID_INDEX_CACHE_LIMIT) {
    const oldestKey = indexCache.keys().next().value;
    if (oldestKey == null) return;
    indexCache.delete(oldestKey);
  }
}

function touchSearchCache(cache: Map<string, Set<number>>, key: string, value: Set<number>): void {
  cache.delete(key);
  cache.set(key, value);

  while (cache.size > SEARCH_CACHE_LIMIT) {
    const oldestKey = cache.keys().next().value;
    if (oldestKey == null) return;
    cache.delete(oldestKey);
  }
}

function buildIndex(customerId: number | null, signature: string, videos: VideoViewData[]): VideoGridIndex {
  const idToVideo = new Map<number, VideoViewData>();
  const sourceOrderIds: number[] = [];
  const records: DatasetRecord[] = [];

  for (const video of videos) {
    const id = video.media.mediaId;
    idToVideo.set(id, video);
    sourceOrderIds.push(id);
    records.push({
      id,
      source: video,
      title: video.media.title,
      // The list API omits empty fields, so these can be undefined at runtime
      // even though the wire types declare string | null / string[].
      refId: video.media.refId ?? null,
      descShort: video.media.descShort ?? null,
      descLong: video.media.descLong ?? null,
      tags: video.tags ?? [],
      status: String(video.media.mediaStatusType),
      state: String(video.media.mediaStateType),
      published: video.media.published,
      updated: video.media.updated,
      duration: video.media.duration,
      lowerTitle: null,
      lowerRefId: null,
      lowerDescShort: null,
      lowerDescLong: null,
      lowerTags: null,
      statusRank: null,
    });
  }

  return {
    cacheKey: `${customerId ?? 0}:${signature}`,
    signature,
    idToVideo,
    sourceOrderIds,
    sourceOrderIsUpdatedDesc: isSourceOrderUpdatedDesc(videos),
    records,
    sortOrders: {},
    exactSearchCache: new Map(),
    fuzzySearchCache: new Map(),
    filterCache: new Map(),
    fuzzyPreparedRecords: null,
    fuzzyPreparedRefIds: null,
    fuzzyPreparedTags: null,
    fuzzyPreparedDescShorts: null,
    fuzzyPreparedDescLongs: null,
    lastUsedAt: now(),
  };
}

function syncCachedIndex(index: VideoGridIndex, videos: VideoViewData[]): boolean {
  if (index.records.length !== videos.length) {
    return false;
  }

  const nextById = new Map<number, VideoViewData>();
  const nextSourceOrderIds: number[] = [];
  for (const video of videos) {
    nextById.set(video.media.mediaId, video);
    nextSourceOrderIds.push(video.media.mediaId);
  }

  let needsSortReset = false;
  let needsSearchReset = false;
  let needsFilterReset = false;

  for (const record of index.records) {
    const nextVideo = nextById.get(record.id);
    if (!nextVideo) {
      return false;
    }

    if (record.source === nextVideo) {
      continue;
    }

    const nextTitle = nextVideo.media.title;
    const nextRefId = nextVideo.media.refId;
    const nextDescShort = nextVideo.media.descShort ?? null;
    const nextDescLong = nextVideo.media.descLong ?? null;
    const nextTags = nextVideo.tags;
    const nextStatus = String(nextVideo.media.mediaStatusType);
    const nextState = String(nextVideo.media.mediaStateType);
    const nextPublished = nextVideo.media.published;
    const nextUpdated = nextVideo.media.updated;
    const nextDuration = nextVideo.media.duration;

    if (
      record.title !== nextTitle ||
      record.status !== nextStatus ||
      record.published !== nextPublished ||
      record.updated !== nextUpdated
    ) {
      needsSortReset = true;
    }

    if (
      record.title !== nextTitle ||
      record.refId !== nextRefId ||
      record.descShort !== nextDescShort ||
      record.descLong !== nextDescLong ||
      !tagsEqual(record.tags, nextTags)
    ) {
      needsSearchReset = true;
    }

    if (
      record.status !== nextStatus ||
      record.state !== nextState ||
      record.published !== nextPublished ||
      record.duration !== nextDuration ||
      !tagsEqual(record.tags, nextTags)
    ) {
      needsFilterReset = true;
    }

    record.source = nextVideo;
    record.title = nextTitle;
    record.refId = nextRefId;
    record.descShort = nextDescShort;
    record.descLong = nextDescLong;
    record.tags = nextTags;
    record.status = nextStatus;
    record.state = nextState;
    record.published = nextPublished;
    record.updated = nextUpdated;
    record.duration = nextDuration;
    record.lowerTitle = null;
    record.lowerRefId = null;
    record.lowerDescShort = null;
    record.lowerDescLong = null;
    record.lowerTags = null;
    record.statusRank = null;
  }

  index.sourceOrderIds = nextSourceOrderIds;
  index.sourceOrderIsUpdatedDesc = isSourceOrderUpdatedDesc(videos);
  index.idToVideo = nextById;

  if (needsSortReset) {
    index.sortOrders = {};
  }

  if (needsSearchReset) {
    index.exactSearchCache.clear();
    index.fuzzySearchCache.clear();
    index.fuzzyPreparedRecords = null;
    index.fuzzyPreparedRefIds = null;
    index.fuzzyPreparedTags = null;
    index.fuzzyPreparedDescShorts = null;
    index.fuzzyPreparedDescLongs = null;
  }

  if (needsFilterReset) {
    index.filterCache.clear();
  }

  return true;
}

function getOrCreateIndex(customerId: number | null, videos: VideoViewData[]): VideoGridIndexResult {
  const signature = getDatasetSignature(videos);
  const cacheKey = `${customerId ?? 0}:${signature}`;
  const cached = indexCache.get(cacheKey);

  if (cached) {
    const cacheIsValid = syncCachedIndex(cached, videos);
    if (cacheIsValid) {
      touchIndex(cacheKey, cached);
      return { index: cached, cacheHit: true };
    }
    indexCache.delete(cacheKey);
  }

  const index = buildIndex(customerId, signature, videos);
  touchIndex(cacheKey, index);
  return { index, cacheHit: false };
}

function getLowerTitle(record: DatasetRecord): string {
  if (record.lowerTitle !== null) {
    return record.lowerTitle;
  }

  record.lowerTitle = record.title.toLowerCase();
  return record.lowerTitle;
}

function getLowerRefId(record: DatasetRecord): string | null {
  if (record.refId == null || record.refId.length === 0) {
    return null;
  }
  if (record.lowerRefId !== null) {
    return record.lowerRefId;
  }

  record.lowerRefId = record.refId.toLowerCase();
  return record.lowerRefId;
}

function getLowerDescShort(record: DatasetRecord): string | null {
  if (record.descShort == null || record.descShort.length === 0) {
    return null;
  }
  if (record.lowerDescShort !== null) {
    return record.lowerDescShort;
  }

  record.lowerDescShort = record.descShort.toLowerCase();
  return record.lowerDescShort;
}

function getLowerDescLong(record: DatasetRecord): string | null {
  if (record.descLong == null || record.descLong.length === 0) {
    return null;
  }
  if (record.lowerDescLong !== null) {
    return record.lowerDescLong;
  }

  record.lowerDescLong = record.descLong.toLowerCase();
  return record.lowerDescLong;
}

function getLowerTags(record: DatasetRecord): string[] {
  if (record.lowerTags !== null) {
    return record.lowerTags;
  }

  record.lowerTags = record.tags.map((tag) => tag.toLowerCase());
  return record.lowerTags;
}

function getRecordStatusRank(record: DatasetRecord): number {
  if (record.statusRank !== null) {
    return record.statusRank;
  }

  record.statusRank = getStatusRank(record.status);
  return record.statusRank;
}

function compareRecordAsc(a: DatasetRecord, b: DatasetRecord, key: VideoSortKey): number {
  switch (key) {
    case 'title': {
      const aTitle = getLowerTitle(a);
      const bTitle = getLowerTitle(b);
      if (aTitle < bTitle) return -1;
      if (aTitle > bTitle) return 1;
      return a.id - b.id;
    }
    case 'status': {
      const aStatus = getRecordStatusRank(a);
      const bStatus = getRecordStatusRank(b);
      if (aStatus < bStatus) return -1;
      if (aStatus > bStatus) return 1;
      return a.id - b.id;
    }
    case 'published':
      if (a.published < b.published) return -1;
      if (a.published > b.published) return 1;
      return a.id - b.id;
    case 'updated':
      if (a.updated < b.updated) return -1;
      if (a.updated > b.updated) return 1;
      return a.id - b.id;
    default:
      return a.id - b.id;
  }
}

function ensureSortOrder(index: VideoGridIndex, key: VideoSortKey): { order: SortOrder; buildMs: number } {
  const cached = index.sortOrders[key];
  if (cached) {
    return { order: cached, buildMs: 0 };
  }

  const buildStart = now();
  const ascRecords = [...index.records].sort((a, b) => compareRecordAsc(a, b, key));
  const asc = ascRecords.map((record) => record.id);
  const order: SortOrder = {
    asc,
    desc: [...asc].reverse(),
  };

  index.sortOrders[key] = order;
  return {
    order,
    buildMs: now() - buildStart,
  };
}

function ensureFuzzyPreparedRecords(index: VideoGridIndex): FuzzyPreparedRecord[] {
  if (index.fuzzyPreparedRecords) {
    return index.fuzzyPreparedRecords;
  }

  index.fuzzyPreparedRecords = index.records.map((record) => ({
    id: record.id,
    preparedTitle: fuzzysort.prepare(record.title),
  }));

  return index.fuzzyPreparedRecords;
}

function ensureFuzzyPreparedRefIds(index: VideoGridIndex): FuzzyPreparedRefIdRecord[] {
  if (index.fuzzyPreparedRefIds) {
    return index.fuzzyPreparedRefIds;
  }

  const prepared: FuzzyPreparedRefIdRecord[] = [];
  for (const record of index.records) {
    if (record.refId != null && record.refId.length > 0) {
      prepared.push({
        id: record.id,
        preparedRefId: fuzzysort.prepare(record.refId),
      });
    }
  }

  index.fuzzyPreparedRefIds = prepared;
  return prepared;
}

function ensureFuzzyPreparedField(
  index: VideoGridIndex,
  key: 'fuzzyPreparedTags' | 'fuzzyPreparedDescShorts' | 'fuzzyPreparedDescLongs',
  getText: (record: DatasetRecord) => string | null
): FuzzyPreparedFieldRecord[] {
  const cached = index[key];
  if (cached) {
    return cached;
  }

  const prepared: FuzzyPreparedFieldRecord[] = [];
  for (const record of index.records) {
    const text = getText(record);
    if (text != null && text.length > 0) {
      prepared.push({
        id: record.id,
        prepared: fuzzysort.prepare(text),
      });
    }
  }

  index[key] = prepared;
  return prepared;
}

function getExactMatchSet(index: VideoGridIndex, queryLower: string): Set<number> {
  const cached = index.exactSearchCache.get(queryLower);
  if (cached) {
    touchSearchCache(index.exactSearchCache, queryLower, cached);
    return cached;
  }

  // Legacy-parity field set (MediaSearch.searchByRegex): title, refId, tags,
  // descShort, descLong — ordered cheapest first so the common title hit
  // short-circuits the rest.
  const matched = new Set<number>();
  for (const record of index.records) {
    if (getLowerTitle(record).includes(queryLower)) {
      matched.add(record.id);
      continue;
    }
    const lowerRefId = getLowerRefId(record);
    if (lowerRefId !== null && lowerRefId.includes(queryLower)) {
      matched.add(record.id);
      continue;
    }
    if (record.tags.length > 0 && getLowerTags(record).some((tag) => tag.includes(queryLower))) {
      matched.add(record.id);
      continue;
    }
    const lowerDescShort = getLowerDescShort(record);
    if (lowerDescShort !== null && lowerDescShort.includes(queryLower)) {
      matched.add(record.id);
      continue;
    }
    const lowerDescLong = getLowerDescLong(record);
    if (lowerDescLong !== null && lowerDescLong.includes(queryLower)) {
      matched.add(record.id);
    }
  }

  touchSearchCache(index.exactSearchCache, queryLower, matched);
  return matched;
}

function getFuzzyMatchSet(index: VideoGridIndex, query: string): Set<number> {
  const queryKey = query.trim().toLowerCase();
  const cached = index.fuzzySearchCache.get(queryKey);
  if (cached) {
    touchSearchCache(index.fuzzySearchCache, queryKey, cached);
    return cached;
  }

  const titleResults = fuzzysort.go(query, ensureFuzzyPreparedRecords(index), {
    key: 'preparedTitle',
    threshold: -10000,
    limit: Infinity,
  });

  const matched = new Set<number>();
  for (const result of titleResults) {
    matched.add(result.obj.id);
  }

  const preparedRefIds = ensureFuzzyPreparedRefIds(index);
  if (preparedRefIds.length > 0) {
    const refIdResults = fuzzysort.go(query, preparedRefIds, {
      key: 'preparedRefId',
      threshold: -10000,
      limit: Infinity,
    });
    for (const result of refIdResults) {
      matched.add(result.obj.id);
    }
  }

  // Legacy-parity fields: tags (joined per record), descShort, descLong.
  const fieldLists: FuzzyPreparedFieldRecord[][] = [
    ensureFuzzyPreparedField(index, 'fuzzyPreparedTags', (record) =>
      record.tags.length > 0 ? record.tags.join(' ') : null
    ),
    ensureFuzzyPreparedField(index, 'fuzzyPreparedDescShorts', (record) => record.descShort),
    ensureFuzzyPreparedField(index, 'fuzzyPreparedDescLongs', (record) => record.descLong),
  ];
  for (const fieldList of fieldLists) {
    if (fieldList.length === 0) continue;
    const fieldResults = fuzzysort.go(query, fieldList, {
      key: 'prepared',
      threshold: -10000,
      limit: Infinity,
    });
    for (const result of fieldResults) {
      matched.add(result.obj.id);
    }
  }

  touchSearchCache(index.fuzzySearchCache, queryKey, matched);
  return matched;
}

function getFilterMatchSet(index: VideoGridIndex, filters: VideoFilters, filterKey: string): Set<number> {
  const cached = index.filterCache.get(filterKey);
  if (cached) {
    touchSearchCache(index.filterCache, filterKey, cached);
    return cached;
  }

  const prepared = VideoFilterUtils.prepare(filters);
  const matched = new Set<number>();
  // One reused candidate avoids 40k+ short-lived allocations per scan.
  const candidate: VideoFilterCandidate = {
    lowerTags: [],
    published: 0,
    duration: 0,
    state: '',
    status: '',
  };

  for (const record of index.records) {
    candidate.lowerTags = getLowerTags(record);
    candidate.published = record.published;
    candidate.duration = record.duration;
    candidate.state = record.state;
    candidate.status = record.status;
    if (VideoFilterUtils.matchesFilters(candidate, prepared)) {
      matched.add(record.id);
    }
  }

  touchSearchCache(index.filterCache, filterKey, matched);
  return matched;
}

function intersectIdSets(a: Set<number>, b: Set<number>): Set<number> {
  const [small, large] = a.size <= b.size ? [a, b] : [b, a];
  const result = new Set<number>();
  for (const id of small) {
    if (large.has(id)) {
      result.add(id);
    }
  }
  return result;
}

function filterOrder(order: number[], allowed: Set<number>): number[] {
  if (allowed.size === order.length) {
    return order;
  }

  const filtered: number[] = [];
  for (const id of order) {
    if (allowed.has(id)) {
      filtered.push(id);
    }
  }

  return filtered;
}

function computeOrderedIdsMain(
  index: VideoGridIndex,
  search: SearchState,
  sort: SortState,
  filters: VideoFilters,
  filterKey: string
): ComputeResult {
  const totalStart = now();
  const query = search.text.trim();

  const searchStart = now();
  let matchedIds: Set<number> | null = null;
  if (query.length > 0) {
    matchedIds = search.mode === 'exact'
      ? getExactMatchSet(index, query.toLowerCase())
      : getFuzzyMatchSet(index, query);
  }
  const searchMs = now() - searchStart;

  const filterStart = now();
  let allowedIds = matchedIds;
  if (!VideoFilterUtils.isEmpty(filters)) {
    const filteredIds = getFilterMatchSet(index, filters, filterKey);
    allowedIds = matchedIds ? intersectIdSets(matchedIds, filteredIds) : filteredIds;
  }
  const filterMs = now() - filterStart;

  const sortStart = now();
  let sortBuildMs = 0;
  let baseOrder: number[];

  if (sort.key === 'updated' && sort.direction === 'desc' && index.sourceOrderIsUpdatedDesc) {
    baseOrder = index.sourceOrderIds;
  } else {
    const ensuredOrder = ensureSortOrder(index, sort.key);
    sortBuildMs = ensuredOrder.buildMs;
    baseOrder = sort.direction === 'asc' ? ensuredOrder.order.asc : ensuredOrder.order.desc;
  }

  const ids = allowedIds ? filterOrder(baseOrder, allowedIds) : baseOrder;
  const sortMs = now() - sortStart;

  return {
    ids,
    timings: {
      searchMs,
      filterMs,
      sortMs,
      sortBuildMs,
      totalMs: now() - totalStart,
    },
  };
}

function getPersistentRowCache(cacheKey: string): Map<number, RowCacheEntry> {
  const existing = persistedRowCaches.get(cacheKey);
  if (existing) {
    persistedRowCaches.delete(cacheKey);
    persistedRowCaches.set(cacheKey, existing);
    return existing;
  }

  const created = new Map<number, RowCacheEntry>();
  persistedRowCaches.set(cacheKey, created);

  while (persistedRowCaches.size > ROW_CACHE_LIMIT) {
    const oldestKey = persistedRowCaches.keys().next().value;
    if (oldestKey == null) return created;
    persistedRowCaches.delete(oldestKey);
  }

  return created;
}

function reportOrderTimings(timings: TimingStats, meta: Record<string, unknown>): void {
  recordDuration('videos.search.compute', timings.searchMs, meta);
  recordDuration('videos.filter.compute', timings.filterMs, meta);
  recordDuration('videos.sort.compute', timings.sortMs, meta);
  recordDuration('videos.engine.orderCompute', timings.totalMs, meta);
}

export function useVideoGridEngine({
  customerId,
  videos,
  sort,
  search,
  filters,
  linkConfig,
  defaultPlayerId,
  isReseller,
}: UseVideoGridEngineArgs): UseVideoGridEngineResult {
  const setOrderedIdsSnapshot = useVideoGridState((state) => state.setOrderedIdsSnapshot);
  const trimmedSearchText = search.text.trim();
  const filterKey = useMemo(() => VideoFilterUtils.cacheKey(filters), [filters]);
  const orderQueryKey = useMemo(
    () => getOrderQueryKey(sort, search, filterKey),
    [sort.key, sort.direction, search.mode, search.text, filterKey]
  );

  const indexedDataset = useMemo(() => {
    const start = now();
    const result = getOrCreateIndex(customerId, videos);

    recordDuration('videos.engine.datasetBuild', now() - start, {
      count: videos.length,
      cacheHit: result.cacheHit,
      signature: result.index.signature,
      path: 'main',
    });

    return result;
  }, [customerId, videos]);

  const index = indexedDataset.index;
  const rowCache = useMemo(() => {
    const cache = getPersistentRowCache(index.cacheKey);
    for (const id of cache.keys()) {
      if (!index.idToVideo.has(id)) {
        cache.delete(id);
      }
    }
    return cache;
  }, [index.cacheKey, index.idToVideo]);

  useEffect(() => {
    recordSample('videos.engine.path', 0, {
      mode: search.mode,
      queryLength: trimmedSearchText.length,
      count: videos.length,
      key: sort.key,
      direction: sort.direction,
      path: 'main',
    });
  }, [search.mode, trimmedSearchText.length, videos.length, sort.key, sort.direction]);

  const mainResult = useMemo(() => {
    const computed = computeOrderedIdsMain(index, search, sort, filters, filterKey);

    reportOrderTimings(computed.timings, {
      path: 'main',
      mode: search.mode,
      queryLength: trimmedSearchText.length,
      resultCount: computed.ids.length,
      key: sort.key,
      direction: sort.direction,
    });

    return computed;
  }, [index, search.mode, search.text, sort.key, sort.direction, filters, filterKey, trimmedSearchText.length]);

  const orderedIds = mainResult.ids;

  useEffect(() => {
    setOrderedIdsSnapshot(index.signature, orderQueryKey, orderedIds);
  }, [orderedIds, orderQueryKey, index.signature, setOrderedIdsSnapshot]);

  const stats: VideoGridEngineStats = useMemo(
    () => ({
      searchMs: mainResult.timings.searchMs,
      sortMs: mainResult.timings.sortMs,
      totalMs: mainResult.timings.totalMs,
      mode: 'main',
      workerFallback: false,
    }),
    [mainResult.timings.searchMs, mainResult.timings.sortMs, mainResult.timings.totalMs]
  );

  const gridRowContext = useMemo(() => {
    if (videos.length === 0) return null;
    return MediaUtils.getGridRowContext(videos, defaultPlayerId, isReseller);
  }, [videos, defaultPlayerId, isReseller]);

  const contextKey = useMemo(() => {
    if (!gridRowContext) return 'no-context';
    return `${gridRowContext.hostname}:${gridRowContext.cIdBC}:${gridRowContext.playerIdBC}:${defaultPlayerId ?? 'none'}:${linkConfig ? 'with-link' : 'no-link'}`;
  }, [gridRowContext, defaultPlayerId, linkConfig]);

  const getIdByIndex = useCallback(
    (indexValue: number) => orderedIds[indexValue],
    [orderedIds]
  );

  const getRowByIndex = useCallback(
    (indexValue: number): VideoGridRow | null => {
      const id = orderedIds[indexValue];
      if (id == null || !gridRowContext) return null;

      const video = index.idToVideo.get(id);
      if (!video) return null;

      const cached = rowCache.get(id);
      if (cached && cached.source === video && cached.contextKey === contextKey) {
        return cached.row;
      }

      const row = MediaUtils.toGridRow(
        video,
        linkConfig ?? null,
        defaultPlayerId,
        gridRowContext.hostname,
        gridRowContext.cIdBC,
        gridRowContext.playerIdBC
      );

      rowCache.set(id, {
        source: video,
        contextKey,
        row,
      });

      while (rowCache.size > ROW_VIEW_MODEL_CACHE_LIMIT) {
        const oldestId = rowCache.keys().next().value;
        if (oldestId == null) break;
        rowCache.delete(oldestId);
      }

      return row;
    },
    [
      orderedIds,
      gridRowContext,
      index.idToVideo,
      rowCache,
      contextKey,
      linkConfig,
      defaultPlayerId,
    ]
  );

  return {
    count: orderedIds.length,
    orderedIds,
    getIdByIndex,
    getRowByIndex,
    resultCount: orderedIds.length,
    isComputing: false,
    stats,
  };
}
